import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { SearchDefaultsStatus } from "./useSearchDefaultsPrefill";

interface SearchDefaultsBarProps {
	status: SearchDefaultsStatus;
	isLoadingDefaults: boolean;
	canResetToCompanyPreset: boolean;
	onResetToCompanyPreset: () => void;
}

const STATUS_NOTE: Partial<Record<SearchDefaultsStatus, string>> = {
	lastSearch: "fromLastSearch",
	companyPreset: "fromCompanyPreset",
};

/**
 * Says where a new source's filters came from — the user's last search or the company preset —
 * and offers "Reset to company preset" (PREC-3666). A form that fills itself without saying so
 * reads as a form that kept somebody else's filters.
 */
export const SearchDefaultsBar: FunctionComponent<SearchDefaultsBarProps> = ({
	status,
	isLoadingDefaults,
	canResetToCompanyPreset,
	onResetToCompanyPreset,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.searchDefaults.${key}`);

	const noteKey = isLoadingDefaults ? "loading" : STATUS_NOTE[status];

	if (!noteKey && !canResetToCompanyPreset) {
		return null;
	}

	return (
		<div
			className="mt-3 flex items-center justify-between gap-3 text-ssm text-text-meta"
			data-testid="search-defaults-bar"
		>
			<span>{noteKey ? ts(noteKey) : null}</span>
			{canResetToCompanyPreset && (
				<button
					className="shrink-0 cursor-pointer font-medium text-blue-action hover:underline"
					data-testid="reset-to-company-preset"
					type="button"
					onClick={onResetToCompanyPreset}
				>
					{ts("resetToCompanyPreset")}
				</button>
			)}
		</div>
	);
};
